/**
 * anclas.js — Un ancla del pie que no lleva a ninguna sección se RE-APUNTA o se QUITA.
 *
 * Módulo PURO, mismo contrato que pie-enlaces.js: no toca DOM ni `window` y no muta nada. Se
 * ejercita en node (tests/_pie_enlaces.mjs); por eso va en ES2019 sin `?.` ni `??`: el node de los
 * tests es el 12.
 *
 * EL CASO (medido el 28-09-2026 en los 28 sitios de test222): 27 de las 52 anclas del pie no
 * existían, 90 enlaces rotos en 44 páginas. Un ancla muerta no hace nada en la home, y desde una
 * subpágina lleva a la home, arriba del todo. Las causas eran dos:
 *   · la IA escribe `#bloque-<tipo>`, que no vale para los tipos con índice: la sección de un
 *     `reservas` se llama `bloque-reservas-7` (utils/secciones.js);
 *   · la IA inventa el id con el nombre de la sección: `#contacto`, `#vibra`, `#bloque-galeria`.
 *
 * DECISIÓN DEL DUEÑO (formulario, 28-09-2026): re-apuntar las que tienen un destino claro y quitar
 * el resto, y hacerlo en CADA sincronización del pie (pie-enlaces.js::sincronizarPie), no una vez:
 * los ids con índice cambian al insertar, borrar o mover un bloque por encima, así que guardar el
 * id resuelto solo aplazaría la próxima ancla muerta. Por niveles, gana el primero que acierta:
 *   1. EXACTA    `bloque-<tipo>` o `bloque-<tipo>-<n>` de un tipo que la página tiene → la sección
 *                de ese tipo (la primera, como hace el navegador con dos ids iguales).
 *   2. SINÓNIMO  una palabra del ancla o del texto nombra un tipo que la página tiene: «Contacto»
 *                → form, «Carta» → menu, «Galería» → gallery (`SINONIMOS`).
 *   3. PÁGINA    una palabra del ancla o del texto nombra OTRA página navegable del mismo idioma,
 *                por su id o su título → el enlace pasa a esa página, sin ancla.
 *   4. NADA      se quita.
 * Un ancla VIVA no se toca nunca. Tampoco los enlaces que no son anclas: externos, tel:/mailto:,
 * `?page=` sin ancla y el `#` con que nace «+ Enlace».
 *
 * Solo lo usa el PIE. La cabecera tiene su propio sincronizador (header-sync.js), sin anclas en
 * los sitios generados: `fusionarNavLinks` descarta las del modelo al crear el sitio.
 */
import { anclasDePagina, primeraSeccion, TIPOS_ID_FIJO, TIPOS_ID_CON_INDICE } from './secciones.js';
import { esPaginaNavegable } from './canonicidad.js';

/**
 * Palabras → tipos de bloque, en orden de preferencia. Cada palabra es una RAÍZ sin acentos y
 * casa con cualquier palabra del ancla o del texto que EMPIECE por ella («galer» = galería,
 * galeria, galerie). Gana la primera fila que case Y cuyo tipo tenga la página; si la página no
 * tiene ninguno de sus tipos, se sigue con la fila siguiente. Idiomas: los del builder (es, ca,
 * en, de, fr, pt). Una raíz corta y ambigua («avis» también es «aviso legal»; «plan», «planta»)
 * no entra: un falso positivo manda al visitante a otra sección. La fila de «inicio» va la
 * última: es la más genérica, y en «Reserva en Home Café» tiene que ganar la de reservas.
 */
const SINONIMOS = [
    [['contact', 'contat', 'kontakt', 'escrib'], ['form', 'reservas', 'reserva-live']],
    [['reserv', 'book', 'buch', 'cita'], ['reservas', 'reserva-live', 'form']],
    // OJO: contacto y reservas tienen que ser las DOS PRIMERAS filas (`FILAS_DE_CONTACTO`, `paginaDeContacto`). Las
    // entradas van detrás: puestas en medio, «Reservar» dejaba de buscar la página de reservas (fusión del 23-10).
    [['entrad', 'ticket', 'billet', 'bilhet', 'bigliet', 'sarrer'], ['entradas']],
    [['carta', 'menu', 'speisekarte', 'cardapio'], ['menu']],
    [['galer', 'gallery', 'foto', 'photo'], ['gallery']],
    [['testimon', 'opinion', 'resen', 'review', 'comunidad'], ['testimonials', 'reviews']],
    [['ubicac', 'localiz', 'location', 'donde', 'mapa', 'llegar', 'direcc', 'adresse', 'anfahrt'], ['maps']],
    [['precio', 'preu', 'price', 'pricing', 'preis', 'prix', 'tarifa', 'planes', 'plans'], ['pricing']],
    [['faq', 'pregunta', 'question'], ['faq']],
    [['equip', 'team'], ['team']],
    [['servici', 'servei', 'service', 'leistung'], ['services', 'features']],
    [['proceso', 'process', 'metodolog', 'funciona', 'pasos'], ['process']],
    [['marca', 'brand', 'cliente', 'partner'], ['logos']],
    [['catalog', 'product'], ['catalogo']],
    [['historia', 'history', 'trayectoria'], ['timeline']],
    [['cifra', 'impacto', 'numero'], ['stats']],
    [['inicio', 'inici', 'home', 'portada', 'startseite', 'accueil', 'hero'], ['hero', 'carrusel']],
];

/**
 * Palabras que no identifican ninguna página («Nuestra Vibra» no es la página «Nuestra Carta»).
 * Las de menos de 4 letras ya no cuentan (`_utiles`).
 */
/** Las dos primeras filas de SINONIMOS: contacto y reservas (las que piden un formulario). */
const FILAS_DE_CONTACTO = 2;

/**
 * Otra página navegable de `paginas` (menos `excluida`) que es de CONTACTO o de RESERVAS, si alguna
 * palabra lo pide: «Contactar» → la página «Contacto»; «Reservar» → «Reservas privadas». Solo esas
 * dos filas: «clientes» casaba con una página «Productos y marcas» (utils/destino-boton.js).
 */
export function paginaDeContacto(palabras, paginas, excluida) {
    for (let i = 0; i < FILAS_DE_CONTACTO; i++) {
        const raices = SINONIMOS[i][0];
        if (!casan(palabras, raices)) continue;
        for (let j = 0; j < paginas.length; j++) {
            const p = paginas[j];
            if (!p || p.id === excluida || !esPaginaNavegable(p)) continue;
            if (casan(palabrasDe(p.id + ' ' + (p.title || '')), raices)) return p.id;
        }
    }
    return null;
}

const VACIAS = [
    'bloque', 'seccion', 'section', 'pagina', 'page', 'nuestra', 'nuestro', 'nuestras', 'nuestros',
    'nostra', 'nostre', 'nostres', 'unsere', 'unser', 'notre', 'nossa', 'nosso', 'sobre', 'about',
    'todas', 'todos', 'conoce', 'descubre', 'para', 'como', 'desde', 'with', 'your', 'from', 'more',
    'this', 'that', 'aqui', 'here', 'mehr', 'plus', 'nous',
];

const TIPOS_CON_ID = TIPOS_ID_FIJO.concat(TIPOS_ID_CON_INDICE);

/** Texto → palabras en minúsculas, sin acentos, sin etiquetas y partidas por lo que no es letra. */
export function palabrasDe(texto) {
    let s = String(texto == null ? '' : texto).replace(/<[^>]*>/g, ' ');
    try { s = decodeURIComponent(s); } catch (e) { /* un «%» suelto: se queda como está */ }
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
        .split(/[^a-z0-9]+/).filter(Boolean);
}

/** El tipo de un ancla `bloque-<tipo>` o `bloque-<tipo>-<n>`, o null si no es de esa forma. */
function _tipoDelAncla(ancla) {
    const a = String(ancla).trim().toLowerCase();
    for (let i = 0; i < TIPOS_CON_ID.length; i++) {
        const base = 'bloque-' + TIPOS_CON_ID[i];
        if (a === base) return TIPOS_CON_ID[i];
        if (a.indexOf(base + '-') === 0 && /^\d+$/.test(a.slice(base.length + 1))) return TIPOS_CON_ID[i];
    }
    return null;
}

/** ¿Alguna palabra empieza por alguna de las raíces? */
export function casan(palabras, raices) {
    return palabras.some(w => raices.some(r => w.indexOf(r) === 0));
}

/**
 * La sección de `page` que nombra alguna palabra (nivel SINÓNIMO), o null. Con `sinInicio` se salta
 * la fila de «inicio» (hero/carrusel): un BOTÓN que lleva arriba del todo es lo mismo que el `#` que
 * ya tiene (utils/destino-boton.js).
 */
export function seccionQueNombra(page, palabras, sinInicio) {
    for (let i = 0; i < SINONIMOS.length; i++) {
        if (sinInicio && SINONIMOS[i][1].indexOf('hero') >= 0) continue;
        if (!casan(palabras, SINONIMOS[i][0])) continue;
        const id = primeraSeccion(page, SINONIMOS[i][1]);
        if (id) return id;
    }
    return null;
}

/** La primera página navegable de `paginas` (menos `excluida`) que nombra alguna palabra útil. */
export function paginaQueNombra(palabras, paginas, excluida) {
    const utiles = palabras.filter(w => w.length >= 4 && VACIAS.indexOf(w) === -1);
    if (!utiles.length) return null;
    for (let i = 0; i < paginas.length; i++) {
        const p = paginas[i];
        if (!p || p.id === excluida || !esPaginaNavegable(p)) continue;
        if (casan(palabrasDe(p.id + ' ' + (p.title || '')), utiles)) return p.id;
    }
    return null;
}

/**
 * Un enlace propio del pie en forma CANÓNICA (canonicidad.js::propiosCanonicos: `{text, url}` o
 * `{text, pagina, ancla}`), con su ancla resuelta. Devuelve el mismo objeto si no hay nada que
 * hacer, uno nuevo si se re-apunta y `null` si hay que quitarlo.
 *
 * @param {object} ref
 * @param {Map<string, object>} porId   Todas las páginas del sitio por id.
 * @param {Array<object>} delIdioma     Las páginas del idioma del pie (candidatas del nivel 3).
 */
export function resolverAncla(ref, porId, delIdioma) {
    if (!ref || ref.url !== undefined || !ref.ancla) return ref;
    // Sin su página no se sabe si el ancla vive: no se toca. Desde sincronizarPie no pasa (los
    // `?page=` a páginas que no existen ya los quitó `propiosCanonicos`), y quitarlo aquí borraría
    // enlaces buenos si alguien pasara un `porId` a medias.
    const page = porId.get(ref.pagina);
    if (!page || anclasDePagina(page).has(ref.ancla)) return ref;

    const tipo = _tipoDelAncla(ref.ancla);
    const exacta = tipo ? primeraSeccion(page, [tipo]) : null;
    if (exacta) return { text: ref.text, pagina: ref.pagina, ancla: exacta };

    const palabras = palabrasDe(ref.ancla).concat(palabrasDe(ref.text));
    const sinonimo = seccionQueNombra(page, palabras);
    if (sinonimo) return { text: ref.text, pagina: ref.pagina, ancla: sinonimo };

    const otra = paginaQueNombra(palabras, delIdioma || [], ref.pagina);
    return otra ? { text: ref.text, pagina: otra, ancla: '' } : null;
}
